import React, { useEffect, useState } from "react";
import { Copy, Loader2, MessageSquare, Send } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AiPageShell from "../../components/ai/AiPageShell";
import { akilMesajTaslagi, akilWhatsappKuyruk } from "../../lib/akilApi";
import { getAkilContext } from "../../lib/akilContext";

const SABLONLAR = [
    ["eksik_evrak", "Eksik evrak hatırlatması"],
    ["serbest", "Serbest cevap (yapıştır)"],
];

export default function AkilMesajTaslagiPage() {
    const [searchParams] = useSearchParams();
    const [kanal, setKanal] = useState("whatsapp");
    const [sablon, setSablon] = useState("eksik_evrak");
    const [gelen, setGelen] = useState("");
    const [dosyaId, setDosyaId] = useState("");
    const [dosyaNo, setDosyaNo] = useState("");
    const [ton, setTon] = useState("profesyonel");
    const [metin, setMetin] = useState("");
    const [eksikEvrak, setEksikEvrak] = useState([]);
    const [loading, setLoading] = useState(false);
    const [kuyrukLoading, setKuyrukLoading] = useState(false);

    useEffect(() => {
        const ctx = getAkilContext();
        const qId = searchParams.get("dosya_id") || searchParams.get("sigorta_dosyasi_id");
        const qSablon = searchParams.get("sablon");
        if (qId) setDosyaId(String(qId));
        else if (ctx?.sigorta_dosyasi_id) setDosyaId(String(ctx.sigorta_dosyasi_id));
        if (ctx?.dosya_no) setDosyaNo(ctx.dosya_no);
        if (qSablon) setSablon(qSablon);
        else if (ctx?.sigorta_dosyasi_id) setSablon("eksik_evrak");
    }, [searchParams]);

    const uret = async () => {
        if (sablon === "serbest" && !gelen.trim()) {
            toast.error("Gelen mesajı yapıştırın.");
            return;
        }
        if (!dosyaId.trim() && sablon === "eksik_evrak") {
            toast.error("Dosya ID gerekli — dosya detayından açın veya ID girin.");
            return;
        }
        setLoading(true);
        try {
            const data = await akilMesajTaslagi({
                kanal,
                sablon: sablon === "serbest" ? undefined : sablon,
                gelen_mesaj: sablon === "serbest" ? gelen : undefined,
                sigorta_dosyasi_id: dosyaId.trim() ? Number(dosyaId) : undefined,
                ton,
            });
            if (!data.success) throw new Error(data.error);
            setMetin(data.metin || "");
            setEksikEvrak(data.eksik_evrak || []);
            if (data.dosya_no) setDosyaNo(data.dosya_no);
            toast.success(`${data.kanal || "Mesaj"} taslağı hazır.`);
        } catch (err) {
            toast.error(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const kuyrugaEkle = async () => {
        if (!metin.trim()) {
            toast.error("Önce taslak üretin.");
            return;
        }
        setKuyrukLoading(true);
        try {
            const data = await akilWhatsappKuyruk({
                metin,
                sigorta_dosyasi_id: dosyaId.trim() ? Number(dosyaId) : undefined,
            });
            if (!data.success) throw new Error(data.error || data.detail);
            toast.success("WhatsApp Merkez onay kuyruğuna eklendi.");
        } catch (err) {
            toast.error(err.response?.data?.error || err.message);
        } finally {
            setKuyrukLoading(false);
        }
    };

    return (
        <AiPageShell
            title="Mesaj Taslağı"
            subtitle="Dosya bağlamında eksik evrak hatırlatması veya serbest cevap — WhatsApp kuyruğuna onaylı gönderim."
        >
            {dosyaNo ? (
                <p className="text-xs text-[#D4AF37]/90 mb-4">
                    Bağlam: <strong>{dosyaNo}</strong>
                    {dosyaId ? ` (#${dosyaId})` : ""}
                </p>
            ) : null}

            <div className="grid lg:grid-cols-2 gap-6">
                <div className="rounded-2xl border border-white/10 bg-[#121A2F]/60 p-5 space-y-4">
                    <div className="flex flex-wrap gap-2">
                        {SABLONLAR.map(([v, l]) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setSablon(v)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                                    sablon === v
                                        ? "bg-[#D4AF37]/20 border-[#D4AF37]/40 text-[#F3E5AB]"
                                        : "border-white/10 text-slate-400"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        {[
                            ["whatsapp", "WhatsApp"],
                            ["email", "E-posta"],
                        ].map(([v, l]) => (
                            <button
                                key={v}
                                type="button"
                                onClick={() => setKanal(v)}
                                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                                    kanal === v
                                        ? "bg-[#D4AF37]/20 border-[#D4AF37]/40 text-[#F3E5AB]"
                                        : "border-white/10 text-slate-400"
                                }`}
                            >
                                {l}
                            </button>
                        ))}
                    </div>
                    {sablon === "serbest" ? (
                        <label className="block text-xs text-slate-400">
                            Gelen mesaj
                            <textarea
                                value={gelen}
                                onChange={(e) => setGelen(e.target.value)}
                                rows={6}
                                className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm"
                                placeholder="Müvekkil veya sigorta şirketinden gelen metni buraya yapıştırın"
                            />
                        </label>
                    ) : (
                        <p className="text-xs text-slate-500 rounded-lg border border-white/10 bg-black/20 p-3">
                            7 zorunlu evrak listesinden kural tabanlı hatırlatma metni üretilir (AI uydurmaz).
                        </p>
                    )}
                    <label className="block text-xs text-slate-400">
                        Sigorta dosyası ID
                        <input
                            value={dosyaId}
                            onChange={(e) => setDosyaId(e.target.value)}
                            className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm"
                            placeholder="Dosya detayından otomatik dolar"
                        />
                    </label>
                    <label className="block text-xs text-slate-400">
                        Üslup
                        <select
                            value={ton}
                            onChange={(e) => setTon(e.target.value)}
                            className="mt-1 w-full rounded-lg bg-[#0B0F19] border border-white/10 px-3 py-2 text-sm"
                        >
                            <option value="profesyonel">Profesyonel</option>
                            <option value="samimi">Samimi</option>
                            <option value="kisa">Kısa ve net</option>
                        </select>
                    </label>
                    <button
                        type="button"
                        disabled={loading}
                        onClick={uret}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#D4AF37] text-black text-sm font-bold disabled:opacity-50"
                    >
                        {loading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <MessageSquare className="h-4 w-4" />
                        )}
                        Taslak Üret
                    </button>
                </div>

                <div className="rounded-2xl border border-white/10 bg-[#121A2F]/60 p-5 flex flex-col min-h-[320px]">
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                        <h2 className="text-sm font-semibold text-[#F3E5AB]">Önerilen cevap</h2>
                        <div className="flex gap-2">
                            {metin ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        navigator.clipboard.writeText(metin);
                                        toast.success("Kopyalandı.");
                                    }}
                                    className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-white"
                                >
                                    <Copy className="h-3.5 w-3.5" /> Kopyala
                                </button>
                            ) : null}
                            {metin && kanal === "whatsapp" ? (
                                <button
                                    type="button"
                                    disabled={kuyrukLoading}
                                    onClick={kuyrugaEkle}
                                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                                >
                                    {kuyrukLoading ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : (
                                        <Send className="h-3.5 w-3.5" />
                                    )}
                                    WhatsApp kuyruğu
                                </button>
                            ) : null}
                        </div>
                    </div>
                    {eksikEvrak.length ? (
                        <p className="text-[11px] text-slate-500 mb-2">
                            Eksik (7&apos;li liste): {eksikEvrak.join(", ")}
                        </p>
                    ) : null}
                    <textarea
                        value={metin}
                        onChange={(e) => setMetin(e.target.value)}
                        className="flex-1 min-h-[240px] w-full rounded-xl bg-[#0B0F19] border border-white/10 px-4 py-3 text-sm leading-relaxed resize-y"
                    />
                    <Link
                        to="/yeni/whatsapp-merkezi"
                        className="mt-3 text-xs text-[#D4AF37] hover:underline"
                    >
                        WhatsApp Merkezi →
                    </Link>
                </div>
            </div>
        </AiPageShell>
    );
}
